import axios from "axios";
import { createSlice } from "@reduxjs/toolkit";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { API_ENDPOINT } from "../../../config/config";

const initialState = {
  loading: false,
  requestId: null,
  count: 0,
  currentPage: 1,
  limit: 18,
  offset: 0,
  results: [],
  search: "",
  position: "",
  colleage: "",
  board: null,
};

// iOL also takes centers (iOL_C).
const positionQuery = (position) => {
  if (!position) return "";
  if (position === "iOL") return "&position=iOL&position=iOL_C";
  return `&position=${encodeURIComponent(position)}`;
};

// The public Players list reads the big board published in admin (bigboard/), not players/.
// Same filters and fields, but its ids are big-board ids — not the player ids the simulator uses.
//
// Every list request goes through here, with search, position and school read from state, so the
// filters always travel together. They used to have a request each, and some dropped the others:
// clearing the position loaded every school while the school dropdown still showed one.
export const fetchPlayers = createAsyncThunk(
  "players/fetchPlayers",
  async (page = 1, { getState, rejectWithValue }) => {
    try {
      const {
        players: { limit, search, position, colleage },
      } = getState();
      const offset = (page - 1) * limit;
      const res = await axios.get(
        `${API_ENDPOINT}bigboard/?limit=${limit}&offset=${offset}&search=${encodeURIComponent(search)}${positionQuery(position)}&school=${encodeURIComponent(colleage)}`
      );
      return { ...res.data, currentPage: page, offset };
    } catch (error) {
      if (error.response && error.response.data.message) {
        return rejectWithValue(error.response.data.message);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const playersSlice = createSlice({
  name: "players",
  initialState,
  reducers: {
    setSearch: (state, action) => {
      state.search = action.payload
    },
    setPosition: (state, action) => {
      state.position = action.payload
    },
    setColleage: (state, action) => {
      state.colleage = action.payload
    },
    resetFilters: (state) => {
      state.search = "";
      state.position = "";
      state.colleage = "";
    },
  },
  extraReducers: {
    // Only the latest request may write: two quick filter changes can answer out of order, and the
    // older answer would otherwise land last.
    [fetchPlayers.pending]: (state, action) => {
      state.loading = true;
      state.requestId = action.meta.requestId;
    },
    [fetchPlayers.fulfilled]: (state, action) => {
      if (action.meta.requestId !== state.requestId) return;
      state.loading = false;
      state.count = action.payload.count;
      state.results = action.payload.results;
      state.currentPage = action.payload.currentPage;
      state.offset = action.payload.offset;
      // bigboard/ sends the published board ({ id, name, published_at }) with every page.
      state.board = action.payload.board ?? state.board;
    },
    [fetchPlayers.rejected]: (state, action) => {
      if (action.meta.requestId !== state.requestId) return;
      state.loading = false;
    },
  },
});

export const selectPlayers = (state) => state.players;
export const { setSearch, setPosition, setColleage, resetFilters } = playersSlice.actions;

// Action Creator Thunk
// A fresh visit starts unfiltered: the search box starts empty, so filters left in the store from
// the last visit would no longer match what the page shows.
export const getPlayers = () => (dispatch) => {
  dispatch(resetFilters());
  dispatch(fetchPlayers(1));
};

export const pageNav = (page) => (dispatch) => {
  dispatch(fetchPlayers(page));
};

// Each filter change stores its value first (the dropdown shows it at once, while the list loads)
// and goes back to page 1.
export const searchPlayers = (search) => (dispatch) => {
  dispatch(setSearch(search));
  dispatch(fetchPlayers(1));
};

export const positionAction = (positionValue) => (dispatch) => {
  dispatch(setPosition(positionValue));
  dispatch(fetchPlayers(1));
};

export const colleageAction = (colleageValue) => (dispatch) => {
  dispatch(setColleage(colleageValue));
  dispatch(fetchPlayers(1));
};
export default playersSlice.reducer;
